import React, { ChangeEvent, ComponentProps } from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import { useControlled } from '@adsk/alloy-react-helpers';
import theme, {
  getInteractiveStyles,
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

type ToggleProps = Omit<StylableComponent<HTMLDivElement>, 'onChange'> & {
  name?: string;
  checked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  inputProps?: ComponentProps<'input'>;
  onChange?: (val: boolean, e: ChangeEvent<HTMLInputElement>) => void;
  'data-testid'?: string;
};

/**
 * A Toggle
 * Provides a control to turn something on or off.
 * Toggling the switch should enable or select the active state.
 * (Don't phrase toggles with a negative and require clicking to turn something off.)
 * The rest of the props are propogated to the input element.
 */
const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      style,
      className,
      id,
      name,
      checked,
      disabled = false,
      defaultChecked = false,
      onChange: onChangeProp,
      readOnly,
      inputProps = {},
      'data-testid': dataTestid,
      ...props
    },
    ref,
  ) => {
    const [value, handleOnChange] = useControlled<boolean>(
      defaultChecked,
      checked,
      onChangeProp,
    );

    const onChange = readOnly
      ? undefined
      : (e: ChangeEvent<HTMLInputElement>) => {
          !disabled && handleOnChange(!value, e);
        };

    const { style: inputStyle, ...rest } = inputProps;

    return (
      <div
        className={className}
        css={[
          {
            border: '1px solid',
            borderColor: theme.colors.charcoal400,
            borderRadius: theme.borderRadius.pill,
            cursor: 'pointer',
            height: 20,
            position: 'relative',
            width: 37,
            ...(value && {
              backgroundColor: theme.colors.adskBlue500,
              border: 'none',
              ...(disabled && {
                backgroundColor: theme.colors.adskBlue300,
              }),
            }),
            ...(disabled && {
              borderColor: theme.colors.charcoal200,
            }),
            ...getInteractiveStyles(disabled),
            '&:after': {
              backgroundColor: theme.colors.charcoal600,
              ...(disabled && {
                backgroundColor: theme.colors.charcoal200,
              }),
              ...(value && {
                backgroundColor: theme.colors.white,
              }),
              borderRadius: theme.borderRadius.circle,
              content: '""',
              display: 'block',
              height: 12,
              ...(!value && { left: 4 }),
              // left: !value && 4,
              position: 'absolute',
              ...(value && { right: 4 }),
              // right: value && 4,
              top: value ? 4 : 3,
              width: 12,
            },
          },
          style,
        ]}
        {...props}
      >
        <input
          ref={ref}
          id={id}
          name={name}
          role="switch"
          aria-label="toggle"
          aria-checked={value}
          // value={value}
          onChange={onChange}
          type="checkbox"
          checked={value}
          readOnly={readOnly}
          disabled={disabled}
          data-testid={dataTestid}
          css={[
            {
              cursor: 'inherit',
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              margin: 0,
              padding: 0,
              zIndex: 1,
            },
            inputStyle as CSSObject,
          ]}
          {...rest}
        />
      </div>
    );
  },
);

Toggle.displayName = 'Toggle';

Toggle.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The id of the checkbox, used for the label */
  id: PropTypes.string,
  /** The name for the checkbox input */
  name: PropTypes.string,
  /** The value of the toggle */
  checked: PropTypes.bool,
  /** The initial value of the toggle, this will be read only if `checked` is undefined */
  defaultChecked: PropTypes.bool,
  /** onChange handler for the checkbox input */
  onChange: PropTypes.func,
  /** Disables propagation stop */
  readOnly: PropTypes.bool,
  /** Disable checkbox functionallity */
  disabled: PropTypes.bool,
  /** Props passed to the input element */
  inputProps: PropTypes.object,
  /** data-testid passed to the input element */
  'data-testid': PropTypes.string,
};

export default Toggle;
